import { useMemo } from 'react'
import { useResizableColumns } from '@/hooks/use-resizable-columns'
import type { OperationTestResult } from '@/lib/types/test-cases'
import { cn } from '@/lib/utils'
import { failureHeadline } from './failure-panel'
import { SectionLabel } from './section-label'
import { formatOperationCode, stringifyValue } from './value-format'

/** Output cell for an operation: failure headline for runtime errors, else actual. */
function operationOutputDisplay(op: OperationTestResult): string {
  if (op.failure?.kind === 'runtime_error') {
    return failureHeadline(op.failure)
  }
  return stringifyValue(op.actual)
}

interface ClassOperationsTableProps {
  operations: OperationTestResult[]
}

/**
 * Operations table for class-based problems. Renders real execution results
 * up to and including the first failing operation — later operations ran
 * against corrupt state (or were skipped by the stop-early runner) and would
 * mislead. The slice is defensive: Submit already stops early server-side.
 */
export function ClassOperationsTable({
  operations,
}: ClassOperationsTableProps) {
  const visibleOperations = useMemo(() => {
    const firstFailedIndex = operations.findIndex((op) => !op.passed)
    return firstFailedIndex === -1
      ? operations
      : operations.slice(0, firstFailedIndex + 1)
  }, [operations])

  const passedCount = visibleOperations.filter((op) => op.passed).length

  // Calculate optimal column widths based on content
  const initialColumnWidths = useMemo(() => {
    // Find the longest content in each column
    let maxOperationLen = 'Operation'.length
    let maxExpectedLen = 'Expected'.length
    let maxOutputLen = 'Output'.length

    for (let i = 0; i < visibleOperations.length; i++) {
      const op = visibleOperations[i]
      const operationCode = formatOperationCode(op, i)
      const expectedDisplay = stringifyValue(op.expected)
      const actualDisplay = operationOutputDisplay(op)

      maxOperationLen = Math.max(maxOperationLen, operationCode.length)
      maxExpectedLen = Math.max(maxExpectedLen, expectedDisplay.length)
      maxOutputLen = Math.max(maxOutputLen, actualDisplay.length)
    }

    // Convert to proportional widths with some padding
    // Use character count as a proxy for width (monospace font)
    const totalLen = maxOperationLen + maxExpectedLen + maxOutputLen

    // Calculate percentages with minimum thresholds
    let operationPct = Math.max(25, (maxOperationLen / totalLen) * 100)
    let expectedPct = Math.max(15, (maxExpectedLen / totalLen) * 100)
    let outputPct = Math.max(15, (maxOutputLen / totalLen) * 100)

    // Normalize to 100%
    const total = operationPct + expectedPct + outputPct
    operationPct = (operationPct / total) * 100
    expectedPct = (expectedPct / total) * 100
    outputPct = (outputPct / total) * 100

    return { operation: operationPct, expected: expectedPct, output: outputPct }
  }, [visibleOperations])

  // Use the reusable resizable columns hook
  const { gridTemplate, getResizeHandleProps, containerRef, widths } =
    useResizableColumns({
      columns: [
        {
          id: 'operation',
          initialWidth: initialColumnWidths.operation,
          minWidth: 20,
          maxWidth: 70,
        },
        {
          id: 'expected',
          initialWidth: initialColumnWidths.expected,
          minWidth: 10,
          maxWidth: 40,
        },
        {
          id: 'output',
          initialWidth: initialColumnWidths.output,
          minWidth: 10,
          maxWidth: 40,
        },
      ],
    })

  return (
    <div className="space-y-1">
      <SectionLabel>
        Operations ({passedCount}/{visibleOperations.length} passed)
      </SectionLabel>

      <div
        ref={containerRef}
        className="bg-muted/30 border-border/60 overflow-hidden rounded-lg border"
      >
        {/* Header */}
        <div
          className="bg-muted/50 border-border/60 text-muted-foreground border-b text-xs font-semibold uppercase tracking-wide"
          style={{
            display: 'grid',
            gridTemplateColumns: gridTemplate,
          }}
        >
          <span className="px-3 py-2">Operation</span>
          <div className="relative flex items-center">
            {/* Resize handle for operation/expected divider */}
            <div
              className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize touch-none hover:bg-blue-500/50 active:bg-blue-500/70 z-10"
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize operation and expected columns"
              aria-valuenow={widths.operation}
              tabIndex={0}
              {...getResizeHandleProps('operation')}
            />
            <span className="px-3 py-2 text-right w-full">Expected</span>
          </div>
          <div className="relative flex items-center">
            {/* Resize handle for expected/output divider */}
            <div
              className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize touch-none hover:bg-blue-500/50 active:bg-blue-500/70 z-10"
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize expected and output columns"
              aria-valuenow={widths.expected}
              tabIndex={0}
              {...getResizeHandleProps('expected')}
            />
            <span className="px-3 py-2 text-right w-full">Output</span>
          </div>
        </div>

        {/* Operation rows */}
        <div className="divide-border/60 divide-y">
          {visibleOperations.map((op, index) => {
            // Create a stable key from operation method, args, and index
            const operationKey = op.method
              ? `${op.method}-${JSON.stringify(op.args ?? [])}-${index}`
              : `operation-${index}`
            return (
              <OperationRow
                key={operationKey}
                operation={op}
                index={index}
                gridTemplate={gridTemplate}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}

interface OperationRowProps {
  operation: OperationTestResult
  index: number
  gridTemplate: string
}

function OperationRow({ operation, index, gridTemplate }: OperationRowProps) {
  // Construct the operation line of code
  const operationCode = formatOperationCode(operation, index)
  const expectedDisplay = stringifyValue(operation.expected)
  const actualDisplay = operationOutputDisplay(operation)

  return (
    <div
      className={cn(
        'py-1.5 text-sm',
        operation.passed
          ? 'bg-green-500/5 border-l-2 border-l-green-500'
          : 'bg-red-500/5 border-l-2 border-l-red-500',
      )}
      style={{
        display: 'grid',
        gridTemplateColumns: gridTemplate,
      }}
    >
      {/* Operation (method call) */}
      <code
        className="font-mono text-xs text-foreground truncate px-3"
        title={operationCode}
      >
        {operationCode}
      </code>

      {/* Expected */}
      <code
        className="font-mono text-xs text-gray-600 dark:text-gray-400 text-right truncate px-3"
        title={expectedDisplay}
      >
        {expectedDisplay}
      </code>

      {/* Actual */}
      <code
        className={cn(
          'font-mono text-xs text-right truncate px-3',
          operation.passed ? 'text-foreground' : 'text-red-500 font-medium',
        )}
        title={actualDisplay}
      >
        {actualDisplay}
      </code>
    </div>
  )
}
